import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft, CalendarDays, MapPin } from "lucide-react";

import { itemQuery } from "@/lib/queries";
import { RichText, ShareButtons } from "@/components/site/bits";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/trabalhos/$slug")({
  head: ({ loaderData }) => {
    const d = loaderData as any;
    if (!d) {
      return {
        meta: [
          { title: "Trabalho não encontrado | Professor Rurdiney — 40123" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const title = `${d.title} | Professor Rurdiney — 40123`;
    const description = d.summary || "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
      ],
    };
  },
  loader: async ({ context, params }) => {
    const item = await context.queryClient.ensureQueryData(itemQuery("works", params.slug));
    if (!item) throw notFound();
    return item;
  },
  notFoundComponent: () => (
    <div className="flex flex-col items-center justify-center gap-4 px-4 py-32 text-center">
      <h1 className="text-3xl text-balance-title">Trabalho não encontrado</h1>
      <p className="text-muted-foreground">Esse trabalho não existe ou foi removido.</p>
      <Link
        to="/trabalhos"
        className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground"
      >
        <ArrowLeft className="size-4" /> Voltar aos trabalhos
      </Link>
    </div>
  ),
  component: TrabalhoDetail,
});

function TrabalhoDetail() {
  const { slug } = Route.useParams();
  const { data: work } = useSuspenseQuery(itemQuery("works", slug));
  if (!work) return null;

  return (
    <>
      <header className="relative overflow-hidden bg-hero-gradient px-4 pt-28 pb-16 text-primary-foreground md:pt-36 md:pb-20">
        <div className="absolute inset-0 bg-mesh opacity-40" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-4xl">
          <Link
            to="/trabalhos"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-foreground/80 hover:text-primary-foreground"
          >
            <ArrowLeft className="size-4" /> Todos os trabalhos
          </Link>
          <h1 className="mt-6 text-3xl md:text-5xl text-balance-title">{work.title}</h1>
          <div className="mt-5 flex flex-wrap gap-4 text-sm text-primary-foreground/85">
            {work.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="size-4" /> {work.location}
              </span>
            )}
            {work.date && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="size-4" /> {formatDate(work.date)}
              </span>
            )}
          </div>
          {work.summary && (
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-primary-foreground/85">
              {work.summary}
            </p>
          )}
        </div>
      </header>

      <section className="px-4 py-16 md:py-20">
        <div className="mx-auto w-full max-w-4xl">
          <RichText html={work.content} />
          <div className="mt-10">
            <ShareButtons title={work.title} />
          </div>
        </div>
      </section>
    </>
  );
}
